import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "locales";
import "./style.scss";

interface Props {
  onSearch: (query: string) => void;
  onNavigate: (direction: "prev" | "next") => void;
  onClose: () => void;
  currentIndex: number;
  totalResults: number;
}

function ChatSearchBar({ onSearch, onNavigate, onClose, currentIndex, totalResults }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onNavigate(e.shiftKey ? "prev" : "next");
      }
    },
    [onNavigate]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setQuery(val);
      onSearch(val);
    },
    [onSearch]
  );

  const handleClear = useCallback(() => {
    setQuery("");
    onSearch("");
    inputRef.current?.focus();
  }, [onSearch]);

  return (
    <div className="chat-search-bar">
      <div className="chat-search-bar__input-wrap">
        <svg className="chat-search-bar__icon" viewBox="0 0 24 24" width="18" height="18">
          <path
            d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
            fill="currentColor"
          />
        </svg>
        <input
          ref={inputRef}
          className="chat-search-bar__input"
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t("Search messages...")}
        />
        {query && (
          <span className="chat-search-bar__count">
            {totalResults > 0 ? `${currentIndex + 1}/${totalResults}` : t("No results")}
          </span>
        )}
        {query && (
          <button className="chat-search-bar__clear" onClick={handleClear} type="button">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
      <div className="chat-search-bar__nav">
        <button
          className="chat-search-bar__nav-btn"
          onClick={() => onNavigate("prev")}
          disabled={totalResults === 0}
          type="button"
          title={t("Previous (Shift+Enter)")}
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" fill="currentColor" />
          </svg>
        </button>
        <button
          className="chat-search-bar__nav-btn"
          onClick={() => onNavigate("next")}
          disabled={totalResults === 0}
          type="button"
          title={t("Next (Enter)")}
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
          </svg>
        </button>
      </div>
      <button className="chat-search-bar__close" onClick={onClose} type="button">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
        </svg>
      </button>
    </div>
  );
}

export default ChatSearchBar;
